import { useState } from "react";
import {
  Stethoscope, Search, Scale, Phone, ClipboardPlus, Heart,
  Pill, BookOpen, ChevronRight, ChevronLeft, Check, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const STEPS = [
  {
    title: "Welcome to YEMS Clinical Guidelines",
    subtitle: "Your digital EMS clinical guidelines",
    iconKey: "stethoscope",
    color: "text-primary",
    content: "This app gives you instant access to all Yukon EMS clinical guidelines, drug monographs, procedures, and interactive clinical tools — designed to work offline in the field.",
  },
  {
    title: "Choose Your Guideline Set",
    subtitle: "Protocols tailored to your service",
    iconKey: "book",
    color: "text-blue-400",
    content: "Start by selecting your guideline set from the Service Selector: Ground (PCP/ACP-G), SFA/EMR, PCP-PS, or Air Ambulance. Each set shows only the protocols and care levels relevant to your scope of practice.",
  },
  {
    title: "Search Everything",
    subtitle: "Protocols, drugs, and procedures in one search",
    iconKey: "search",
    color: "text-emerald-400",
    content: "The search bar at the top finds protocols, drug monographs, and procedures all at once. Results are color-coded — blue for protocols, amber for drugs, green for procedures. Tap any result to jump directly to it.",
  },
  {
    title: "Weight-Based Dosing",
    subtitle: "Enter weight once, use everywhere",
    iconKey: "scale",
    color: "text-amber-400",
    content: "Enter the patient's weight in the top-right corner (kg). It automatically applies to all weight-based dose calculators, the Broselow estimator in pediatric protocols, and the BSA burn calculator.",
  },
  {
    title: "Intervention Tracker",
    subtitle: "Log drugs and interventions for your PCR",
    iconKey: "clipboard",
    color: "text-emerald-400",
    content: "Tap the clipboard icon in the header or open Intervention Log from the sidebar. Log drugs with dose, unit, route, and time. Log interventions like '12-Lead ECG' or 'IV Access'. Copy or print the log for your PCR after the call.",
  },
  {
    title: "Code Timer",
    subtitle: "CPR cycle tracking with metronome",
    iconKey: "heart",
    color: "text-red-400",
    content: "Available on cardiac arrest protocols or from the sidebar. Tracks 2-minute CPR cycles, prompts rhythm checks, logs shocks, and includes an audio metronome. Advances automatically when a shock is delivered early.",
  },
  {
    title: "Phone Book & Quick Tools",
    subtitle: "Everything you need, one tap away",
    iconKey: "phone",
    color: "text-violet-400",
    content: "The Phone Book has all dispatch, hospital, nursing station, and social services numbers — tap to call. Drug monographs are linked directly from protocol text (highlighted in amber). The GCS, MAP, APGAR, and Shock Index calculators are always available in the protocol header.",
  },
];

function StepIcon({ iconKey, className }) {
  const props = { className };
  switch (iconKey) {
    case "stethoscope": return <Stethoscope {...props} />;
    case "book": return <BookOpen {...props} />;
    case "search": return <Search {...props} />;
    case "scale": return <Scale {...props} />;
    case "clipboard": return <ClipboardPlus {...props} />;
    case "heart": return <Heart {...props} />;
    case "phone": return <Phone {...props} />;
    default: return <Stethoscope {...props} />;
  }
}

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="onboarding-overlay">
      <Card className="max-w-lg w-full shadow-2xl border-primary/20">
        <CardContent className="pt-6 pb-5 px-6">
          {/* Skip */}
          <div className="flex justify-end mb-2">
            <button onClick={onComplete} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="onboarding-skip">
              Skip <X className="w-3 h-3" />
            </button>
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <StepIcon iconKey={current.iconKey} className={`w-8 h-8 ${current.color}`} />
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h2 className="font-heading text-xl font-bold mb-1">{current.title}</h2>
            <p className="text-sm text-muted-foreground mb-3">{current.subtitle}</p>
            <p className="text-sm leading-relaxed">{current.content}</p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === step ? "bg-primary w-6" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(step - 1)}
              disabled={step === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />Back
            </Button>
            {isLast ? (
              <Button size="sm" onClick={onComplete} data-testid="onboarding-finish">
                <Check className="w-4 h-4 mr-1" />Get Started
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(step + 1)} data-testid="onboarding-next">
                Next<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
